import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as ApplicationScreenshotsService from '../services/ApplicationScreenshotsService';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

// Query keys
export const applicationScreenshotsKeys = {
    all: ['application-screenshots'] as const,
    lists: () => [...applicationScreenshotsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...applicationScreenshotsKeys.lists(), { filters }] as const,
    details: () => [...applicationScreenshotsKeys.all, 'detail'] as const,
    detail: (id: string) => [...applicationScreenshotsKeys.details(), id] as const,
};

// Hooks
export const useApplicationScreenshotsQuery = (filters: Record<string, string> = {}) => {
    return useQuery({
        queryKey: applicationScreenshotsKeys.list(filters),
        queryFn: () => ApplicationScreenshotsService.fetchRecords(filters),
    });
};

export const useApplicationScreenshotQuery = (id: string) => {
    return useQuery({
        queryKey: applicationScreenshotsKeys.detail(id),
        queryFn: () => ApplicationScreenshotsService.fetch(id),
        enabled: !!id,
    });
};

export const useCreateApplicationScreenshotMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ApplicationScreenshotsService.add,
        onSuccess: () => {
            notifySuccess('Screenshot created');
            queryClient.invalidateQueries({ queryKey: applicationScreenshotsKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdateApplicationScreenshotMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: import('../types').ApplicationScreenshot }) =>
            ApplicationScreenshotsService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Screenshot updated');
            queryClient.invalidateQueries({ queryKey: applicationScreenshotsKeys.lists() });
            queryClient.invalidateQueries({ queryKey: applicationScreenshotsKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useRetakeApplicationScreenshotMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ApplicationScreenshotsService.retake,
        onSuccess: () => {
            notifySuccess('Screenshot retake initiated');
            queryClient.invalidateQueries({ queryKey: applicationScreenshotsKeys.all });
        },
        onError: notifyError,
    });
};

export const useDeleteApplicationScreenshotMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ApplicationScreenshotsService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Screenshot deleted');
            queryClient.invalidateQueries({ queryKey: applicationScreenshotsKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useApplicationScreenshotsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    // The count drives the badge on FilterRecordsButton. A filter you cannot see
    // is a filter you forget is on, and an empty-string value means "no filter"
    // rather than "match the empty string", so it must not be counted (FNP-1353).
    const activeCount = Object.values(filters)
                              .filter(value => value !== undefined && value !== '').length;

    return {
        filters,
        activeCount,
        handleSetFilters,
        handleClearFilters,
    };
};
